import { useState } from 'react';
import { Send, CheckCircle2, AlertCircle } from 'lucide-react';
import { Link } from 'react-router-dom';
import { useLanguage } from '../i18n/LanguageContext';

// ─────────────────────────────────────────────────────────────
// Contact Form — lean, Finnish-clean, structured intake
// ─────────────────────────────────────────────────────────────

const INDUSTRY_OPTIONS = [
  { value: '', label: '—' },
  { value: 'deep-tech', label: { en: 'Deep Tech (quantum, semi, AI, cyber)', fi: 'Syvätech (kvantti, puolijohde, AI, kyber)' } },
  { value: 'saas-it', label: { en: 'SaaS / IT Services', fi: 'SaaS / IT-palvelut' } },
  { value: 'manufacturing', label: { en: 'Manufacturing / Industrial', fi: 'Teollisuus' } },
  { value: 'cleantech', label: { en: 'Cleantech / Green Energy', fi: 'Cleantech / Vihreä energia' } },
  { value: 'healthcare', label: { en: 'Healthcare / Life Sciences', fi: 'Terveys / Bioalat' } },
  { value: 'financial', label: { en: 'Financial Services', fi: 'Rahoituspalvelut' } },
  { value: 'consumer-retail', label: { en: 'Consumer / Retail', fi: 'Kuluttaja / Vähittäiskauppa' } },
  { value: 'media', label: { en: 'Media / Content', fi: 'Media / Sisältö' } },
  { value: 'public-sector', label: { en: 'Public Sector / Government', fi: 'Julkinen sektori' } },
  { value: 'other', label: { en: 'Other', fi: 'Muu' } },
];

const VOLUME_OPTIONS = [
  { value: '', label: '—' },
  { value: 'pre-revenue', label: { en: 'Pre-revenue', fi: 'Ennen liikevaihtoa' } },
  { value: 'under-1m', label: { en: 'Under €1M', fi: 'Alle 1 M€' } },
  { value: '1m-10m', label: { en: '€1M – €10M', fi: '1–10 M€' } },
  { value: '10m-50m', label: { en: '€10M – €50M', fi: '10–50 M€' } },
  { value: '50m-plus', label: { en: '€50M+', fi: 'Yli 50 M€' } },
  { value: 'not-say', label: { en: 'Prefer not to say', fi: 'En halua kertoa' } },
];

const SERVICE_OPTIONS = [
  { value: 'formation', label: { en: 'Business Formation', fi: 'Yhtiön perustaminen' } },
  { value: 'financial', label: { en: 'Financial Services (Books · VAT · Payroll)', fi: 'Talouspalvelut (kirjanpito · ALV · palkat)' } },
  { value: 'hr', label: { en: 'HR & Recruitment', fi: 'HR & rekrytointi' } },
  { value: 'admin', label: { en: 'Administrative Support', fi: 'Hallinnollinen tuki' } },
  { value: 'market-entry', label: { en: 'Market Entry Consulting', fi: 'Markkinatulokonsultointi' } },
  { value: 'grants', label: { en: 'Grants & Growth Advisory', fi: 'Avustukset & kasvuneuvonta' } },
];

const initialState = {
  name: '',
  email: '',
  phone: '',
  company: '',
  industry: '',
  businessVolume: '',
  expectedServices: [],
  vision: '',
  consent: false,
};

const ContactForm = () => {
  const { language } = useLanguage();
  const [state, setState] = useState(initialState);
  const [status, setStatus] = useState('idle'); // idle | loading | success | error
  const [errorMsg, setErrorMsg] = useState('');

  const L = {
    en: {
      heading: 'Tell us about your Finland move.',
      sub: 'A founding-team member replies personally — usually within one business day.',
      groupYou: 'You',
      groupBusiness: 'Your business',
      groupIntent: 'Your intent',
      name: 'Full name',
      email: 'Work email',
      phone: 'Phone (with country code)',
      phoneHint: 'Optional — helps us reach back faster.',
      company: 'Company (optional)',
      industry: 'Industry',
      volume: 'Annual revenue',
      services: 'Services you expect',
      servicesHint: 'Pick as many as apply.',
      vision: 'Your vision for Finland',
      visionPlaceholder: 'Timeline, target customers, market entry goals, or any concerns we can address.',
      consent: 'I agree that 2DS Finland Oy may store and process this information to respond to my inquiry, per the',
      consentLink: 'Privacy Policy',
      submit: 'Send to the founding team',
      submitting: 'Sending…',
      successTitle: 'Received.',
      successBody: "Thank you — we'll reply personally within one business day. If it's urgent, use the WhatsApp button on the bottom right.",
      errorTitle: 'Something went wrong.',
      consentError: 'Please accept the privacy notice to send your message.',
      servicesError: 'Please select at least one service.',
    },
    fi: {
      heading: 'Kerro Suomi-suunnitelmastasi.',
      sub: 'Perustajatiimimme jäsen vastaa henkilökohtaisesti — yleensä yhden arkipäivän kuluessa.',
      groupYou: 'Sinä',
      groupBusiness: 'Yrityksesi',
      groupIntent: 'Aikeesi',
      name: 'Koko nimi',
      email: 'Työsähköposti',
      phone: 'Puhelin (maakoodilla)',
      phoneHint: 'Vapaaehtoinen — auttaa meitä tavoittamaan sinut nopeammin.',
      company: 'Yritys (vapaaehtoinen)',
      industry: 'Toimiala',
      volume: 'Vuosiliikevaihto',
      services: 'Odottamasi palvelut',
      servicesHint: 'Valitse niin monta kuin sopii.',
      vision: 'Visiosi Suomesta',
      visionPlaceholder: 'Aikataulu, kohdeasiakkaat, markkinatulon tavoitteet tai huolet, joita voimme käsitellä.',
      consent: 'Hyväksyn, että 2DS Finland Oy tallentaa ja käsittelee näitä tietoja vastatakseen kyselyyni, mukaan',
      consentLink: 'Tietosuojaseloste',
      submit: 'Lähetä perustajatiimille',
      submitting: 'Lähetetään…',
      successTitle: 'Vastaanotettu.',
      successBody: 'Kiitos — vastaamme henkilökohtaisesti yhden arkipäivän kuluessa. Kiireellisissä asioissa käytä WhatsApp-painiketta oikeassa alakulmassa.',
      errorTitle: 'Jotain meni vikaan.',
      consentError: 'Hyväksy tietosuojakäytäntö lähettääksesi viestin.',
      servicesError: 'Valitse vähintään yksi palvelu.',
    },
  }[language] || {};

  const update = (k) => (e) => {
    const val = e.target.type === 'checkbox' ? e.target.checked : e.target.value;
    setState((s) => ({ ...s, [k]: val }));
  };

  const toggleService = (value) => () => {
    setState((s) => ({
      ...s,
      expectedServices: s.expectedServices.includes(value)
        ? s.expectedServices.filter((v) => v !== value)
        : [...s.expectedServices, value],
    }));
  };

  const label = (opt) => (typeof opt.label === 'string' ? opt.label : opt.label[language] || opt.label.en);

  const onSubmit = async (e) => {
    e.preventDefault();
    setErrorMsg('');
    if (!state.consent) {
      setStatus('error');
      setErrorMsg(L.consentError);
      return;
    }
    if (state.expectedServices.length === 0) {
      setStatus('error');
      setErrorMsg(L.servicesError);
      return;
    }
    setStatus('loading');
    try {
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/contact/submit`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: state.name,
          email: state.email,
          phone: state.phone,
          company: state.company,
          industry: state.industry,
          business_volume: state.businessVolume,
          expected_services: state.expectedServices,
          goal: state.vision,
          consent: state.consent,
          locale: language,
        }),
      });
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error(body.detail || `HTTP ${res.status}`);
      }
      setStatus('success');
      setState(initialState);
    } catch (err) {
      setStatus('error');
      setErrorMsg(err.message || 'Network error');
    }
  };

  if (status === 'success') {
    return (
      <div className="bg-white border border-warm rounded-2xl p-8 md:p-10 text-center" data-testid="contact-form-success">
        <div className="w-14 h-14 rounded-full bg-accent-olive/10 flex items-center justify-center mx-auto mb-5">
          <CheckCircle2 className="w-7 h-7 text-accent-olive" />
        </div>
        <h3 className="font-serif text-2xl text-ink mb-3">{L.successTitle}</h3>
        <p className="text-ink-light leading-relaxed">{L.successBody}</p>
      </div>
    );
  }

  const inputCls = "w-full px-4 py-2.5 bg-parchment border border-warm rounded-lg text-ink focus:outline-none focus:ring-2 focus:ring-accent-olive/40 focus:border-accent-olive transition-colors";
  const labelCls = "block text-[11px] uppercase tracking-[0.15em] text-ink-muted mb-1.5";
  const groupHeadingCls = "text-[10px] tracking-[0.25em] uppercase text-accent-olive mb-4 pb-2 border-b border-warm";

  return (
    <form onSubmit={onSubmit} className="bg-white border border-warm rounded-2xl p-6 sm:p-8 md:p-10" data-testid="contact-form">
      <h3 className="font-serif text-2xl sm:text-3xl text-ink mb-2 leading-snug">{L.heading}</h3>
      <p className="text-sm text-ink-light mb-8">{L.sub}</p>

      {/* Group 1 · You */}
      <fieldset className="mb-8">
        <legend className={groupHeadingCls}>{L.groupYou}</legend>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <label className="block">
            <span className={labelCls}>{L.name}</span>
            <input required type="text" value={state.name} onChange={update('name')} className={inputCls} data-testid="contact-input-name" />
          </label>
          <label className="block">
            <span className={labelCls}>{L.email}</span>
            <input required type="email" value={state.email} onChange={update('email')} className={inputCls} data-testid="contact-input-email" />
          </label>
          <label className="block">
            <span className={labelCls}>{L.phone}</span>
            <input
              type="tel"
              value={state.phone}
              onChange={update('phone')}
              placeholder="+358 40 123 4567"
              className={inputCls}
              data-testid="contact-input-phone"
            />
          </label>
          <label className="block">
            <span className={labelCls}>{L.company}</span>
            <input type="text" value={state.company} onChange={update('company')} className={inputCls} data-testid="contact-input-company" />
          </label>
        </div>
      </fieldset>

      {/* Group 2 · Your business */}
      <fieldset className="mb-8">
        <legend className={groupHeadingCls}>{L.groupBusiness}</legend>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <label className="block">
            <span className={labelCls}>{L.industry}</span>
            <select required value={state.industry} onChange={update('industry')} className={inputCls} data-testid="contact-input-industry">
              {INDUSTRY_OPTIONS.map((o) => (
                <option key={o.value} value={o.value} disabled={o.value === ''}>{label(o)}</option>
              ))}
            </select>
          </label>
          <label className="block">
            <span className={labelCls}>{L.volume}</span>
            <select required value={state.businessVolume} onChange={update('businessVolume')} className={inputCls} data-testid="contact-input-volume">
              {VOLUME_OPTIONS.map((o) => (
                <option key={o.value} value={o.value} disabled={o.value === ''}>{label(o)}</option>
              ))}
            </select>
          </label>
        </div>
      </fieldset>

      {/* Group 3 · Your intent */}
      <fieldset className="mb-8">
        <legend className={groupHeadingCls}>{L.groupIntent}</legend>

        {/* Services multi-select */}
        <div className="mb-5">
          <span className={labelCls}>{L.services}</span>
          <p className="text-xs text-ink-muted mb-3 italic">{L.servicesHint}</p>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2" data-testid="contact-services-group">
            {SERVICE_OPTIONS.map((o) => {
              const checked = state.expectedServices.includes(o.value);
              return (
                <button
                  key={o.value}
                  type="button"
                  onClick={toggleService(o.value)}
                  className={`text-left px-3 py-2.5 rounded-lg border text-sm transition-all ${
                    checked
                      ? 'bg-accent-olive/10 border-accent-olive text-ink'
                      : 'bg-parchment border-warm text-ink-light hover:border-accent-olive/50'
                  }`}
                  data-testid={`contact-service-${o.value}`}
                  aria-pressed={checked}
                >
                  <span className="inline-flex items-center gap-2">
                    <span className={`w-3.5 h-3.5 rounded-sm border flex items-center justify-center flex-shrink-0 ${
                      checked ? 'bg-accent-olive border-accent-olive' : 'border-warm'
                    }`}>
                      {checked && <CheckCircle2 className="w-3 h-3 text-parchment" />}
                    </span>
                    <span>{label(o)}</span>
                  </span>
                </button>
              );
            })}
          </div>
        </div>

        <label className="block">
          <span className={labelCls}>{L.vision}</span>
          <textarea
            required
            rows={4}
            value={state.vision}
            onChange={update('vision')}
            placeholder={L.visionPlaceholder}
            className={`${inputCls} resize-none`}
            data-testid="contact-input-vision"
          />
        </label>
      </fieldset>

      {/* Consent */}
      <label className="flex items-start gap-3 mb-5 cursor-pointer">
        <input type="checkbox" checked={state.consent} onChange={update('consent')} data-testid="contact-input-consent" className="mt-1 w-4 h-4 accent-accent-olive" />
        <span className="text-xs text-ink-muted leading-relaxed">
          {L.consent}{' '}
          <Link to="/privacy-policy" className="text-accent-olive underline hover:text-accent-olive-light">
            {L.consentLink}
          </Link>.
        </span>
      </label>

      {status === 'error' && errorMsg && (
        <div className="flex items-start gap-2 mb-4 p-3 bg-accent-rose/10 border border-accent-rose/20 rounded-lg" data-testid="contact-form-error">
          <AlertCircle className="w-4 h-4 text-accent-rose flex-shrink-0 mt-0.5" />
          <p className="text-sm text-accent-rose">{errorMsg}</p>
        </div>
      )}

      <button
        type="submit"
        disabled={status === 'loading'}
        data-testid="contact-submit-btn"
        className="inline-flex items-center gap-2 bg-accent-olive text-white px-6 py-3 rounded-full font-medium hover:bg-accent-olive-light transition-colors disabled:opacity-60"
      >
        <Send className="w-4 h-4" />
        {status === 'loading' ? L.submitting : L.submit}
      </button>
    </form>
  );
};

export default ContactForm;
